import { urlConfig } from "@config/url.config";
import { IRawSettings } from "@core/interfaces/IRawSettings";
import { IRawTelemetry } from "@core/Telemetry";
import AbstractAPI from "./Abstract.api";

export interface IAddDeviceToDbReq {
    token: string;
    imei: string;
    type: string;
    password: string;
    hw_version: string;
    bootloader_version_major: string;
    bootloader_version_minor: string;
    application_version_major: string;
    application_version_minor: string;
}

export interface IGetGroupDevicesReq {
    token: string;
    groupId: string;
    limit?: number;
    offset?: number;
    lastCoord?: boolean;
    telemetry?: boolean;
    filterTelemetry?: string[];
    deviceType?: boolean;
}

export interface IGetGroupDevicesReqOld {
    token: string;
    groupId: string;
}

export interface IGetOneDeviceReq {
    token: string;
    imei: string;
    lastCoord?: boolean;
    telemetry?: boolean;
    filterTelemetry?: string[];
    deviceType?: boolean;
}

export interface IGetOneDeviceBody
    extends Omit<
        IGetOneDeviceReq,
        "lastCoord" | "filterTelemetry" | "deviceType"
    > {
    last_coord?: IGetOneDeviceReq["lastCoord"];
    filter_telemetry?: IGetOneDeviceReq["filterTelemetry"];
    device_type?: IGetOneDeviceReq["deviceType"];
}

export interface IGetGroupDevicesBody
    extends Omit<
        IGetGroupDevicesReq,
        "groupId" | "lastCoord" | "filterTelemetry" | "deviceType"
    > {
    group_id: IGetGroupDevicesReq["groupId"];
    last_coord?: IGetGroupDevicesReq["lastCoord"];
    filter_telemetry?: IGetGroupDevicesReq["filterTelemetry"];
    device_type?: IGetGroupDevicesReq["deviceType"];
}

export interface ILastCoords {
    ts_gps: number | null;
    lat: number | null;
    lon: number | null;
    ts_lbs: number | null;
    lat_lbs: number | null;
    lon_lbs: number | null;
    address?: string;
}

export enum objectTypes {
    human = "human",
    car = "car",
    animal = "animal",
    technique = "technique",
    trailer = "trailer",
}

export enum humanFields {
    fullName = "fullName",
    company = "company",
    companyTaxpayerNumber = "companyTaxpayerNumber",
    jobTitle = "jobTitle",
    callbackPhoneNumber = "callbackPhoneNumber",
}

export enum carFields {
    brand = "brand",
    model = "model",
    color = "color",
    manufactureYear = "manufactureYear",
    carVin = "carVin",
    carNumber = "carNumber",
    inventoryNumber = "inventoryNumber",
    fullName = "fullName",
    company = "company",
    companyTaxpayerNumber = "companyTaxpayerNumber",
    jobTitle = "jobTitle",
    callbackPhoneNumber = "callbackPhoneNumber",
    contractNumber = "contractNumber",
    contractDate = "contractDate",
    deliveryDate = "deliveryDate",
    installationDate = "installationDate",
    fullNameOfInstallingEmployee = "fullNameOfInstallingEmployee",
    installingPlace = "installingPlace",
}

export enum trailerFields {
    brand = "brand",
    model = "model",
    color = "color",
    manufactureYear = "manufactureYear",
    trailerVin = "trailerVin",
    trailerNumber = "trailerNumber",
    inventoryNumber = "inventoryNumber",
    fullName = "fullName",
    company = "company",
    companyTaxpayerNumber = "companyTaxpayerNumber",
    jobTitle = "jobTitle",
    callbackPhoneNumber = "callbackPhoneNumber",
    contractNumber = "contractNumber",
    contractDate = "contractDate",
    deliveryDate = "deliveryDate",
    installationDate = "installationDate",
    fullNameOfInstallingEmployee = "fullNameOfInstallingEmployee",
    installingPlace = "installingPlace",
}

export enum animalFields {
    animalKind = "animalKind",
    animalName = "animalName",
    color = "color",
    birthYear = "birthYear",
    fullName = "fullName",
    company = "company",
    companyTaxpayerNumber = "companyTaxpayerNumber",
    jobTitle = "jobTitle",
    callbackPhoneNumber = "callbackPhoneNumber",
}

export enum techniqueFields {
    techniqueKind = "techniqueKind",
    brand = "brand",
    model = "model",
    color = "color",
    manufactureYear = "manufactureYear",
    inventoryNumber = "inventoryNumber",
    fullName = "fullName",
    company = "company",
    companyTaxpayerNumber = "companyTaxpayerNumber",
    jobTitle = "jobTitle",
    callbackPhoneNumber = "callbackPhoneNumber",
    contractNumber = "contractNumber",
    contractDate = "contractDate",
    deliveryDate = "deliveryDate",
    installationDate = "installationDate",
    fullNameOfInstallingEmployee = "fullNameOfInstallingEmployee",
    installingPlace = "installingPlace",
}

export type objectType = `${objectTypes}`;

type IHumanInfo = {
    [key in humanFields]?: string;
};
type ICarInfo = {
    [key in carFields]?: string;
};
type IAnimalInfo = {
    [key in animalFields]?: string;
};
type ITechniqueInfo = {
    [key in techniqueFields]?: string;
};

type ITrailerInfo = {
    [key in trailerFields]?: string;
};

export interface IAdditionalInfo
    extends IHumanInfo,
        ICarInfo,
        IAnimalInfo,
        ITechniqueInfo,
        ITrailerInfo {}

export interface IToken {
    token: string;
}

export interface IAddDeviceReq extends IToken {
    imei: string;
    name: string;
    icon: number;
    groupId: string;
    password: string;
    type?: objectType | null;
    additionalInfo?: IAdditionalInfo;
}

export interface IAddOldDeviceReq extends IToken {
    name: string;
}

export interface IAddOldDeviceBody {
    token: string;
    name: string;
}

export interface IGetOldDeviceBody {
    token: string;
    groupId: string;
}

export interface IAddOldDeviceReq extends IToken {
    name: string;
}

export interface IAddOldDeviceBody {
    token: string;
    name: string;
}

export interface IGetOldDeviceBody {
    token: string;
    groupId: string;
}

export interface IAddDeviceBody extends Omit<IAddDeviceReq, "groupId"> {
    group_id: IAddDeviceReq["groupId"];
}

export interface IDevice {
    imei: string;
    name: string;
    icon: number;
    type: objectType;
    deviceType: string;
    additionalInfo: IAdditionalInfo;
    telemetry: IRawTelemetry;
    last_coord: ILastCoords;
    address: string;
}

export interface IImei extends Pick<IDevice, "imei"> {}

export interface IDeviceDescription
    extends Pick<IDevice, "name" | "icon" | "type" | "additionalInfo"> {}

export interface IGetGroupDevicesRes {
    total: number;
    devices: IDevice[];
}

export interface IGetGroupDevicesResOld {
    total: number;
    devices: IDevice[];
    groupId: string;
}

export interface IGetAllDevicesRes {
    total: number;
    devices: IImei[];
}

export type DeviceSettingsCommandStatus =
    | "PENDING"
    | "SENDING"
    | "COMPLETED"
    | "FAILED";

export interface IDeviceSettingsCommand {
    id: string;
    status: DeviceSettingsCommandStatus;
    user_id: string | null;
    created_at: string | null;
    sent_at: string | null;
    completed_at: string | null;
    ack_code: string | null;
    failure_reason: string | null;
    settings?: IRawSettings;
}

export interface IGetDeviceSettingsRes {
    settings: IRawSettings;
    command?: IDeviceSettingsCommand | null;
}

export class DevicesAPI extends AbstractAPI {
    async getAllDevices({ token }: IToken) {
        const pathname = "device/getAll";
        const method = "POST";
        const body: IToken = {
            token,
        };
        try {
            return await this._request<IGetAllDevicesRes>(
                pathname,
                method,
                body
            );
        } catch (e) {
            throw new Error(e);
        }
    }
    async getGroupDevices({
        token,
        groupId,
        limit,
        offset,
        lastCoord,
        telemetry,
        filterTelemetry,
        deviceType,
    }: IGetGroupDevicesReq) {
        const pathname = "device/get";
        const method = "POST";
        const body: IGetGroupDevicesBody = {
            token,
            group_id: groupId,
        };
        if (limit) body.limit = limit;
        if (offset) body.offset = offset;
        if (lastCoord) body.last_coord = lastCoord;
        if (telemetry) body.telemetry = telemetry;
        if (filterTelemetry) body.filter_telemetry = filterTelemetry;
        if (deviceType) body.device_type = deviceType;
        try {
            return await this._request<IGetGroupDevicesRes>(
                pathname,
                method,
                body
            );
        } catch (e) {
            throw new Error(e);
        }
    }
    async getOneDevice({
        token,
        imei,
        lastCoord,
        telemetry,
        filterTelemetry,
        deviceType,
    }: IGetOneDeviceReq) {
        const pathname = "device/getOne";
        const method = "POST";
        const body: IGetOneDeviceBody = {
            token,
            imei,
        };
        if (lastCoord) body.last_coord = lastCoord;
        if (telemetry) body.telemetry = telemetry;
        if (filterTelemetry) body.filter_telemetry = filterTelemetry;
        if (deviceType) body.device_type = deviceType;
        try {
            return await this._request<IGetGroupDevicesRes>(
                pathname,
                method,
                body
            );
        } catch (e) {
            throw new Error(e);
        }
    }
    async addDevice({
        token,
        imei,
        name,
        icon,
        groupId,
        password,
        type,
        additionalInfo,
    }: IAddDeviceReq) {
        const pathname = "device/add";
        const method = "POST";
        const body: IAddDeviceBody = {
            token,
            group_id: groupId,
            imei,
            name,
            icon,
            password,
            type,
            additionalInfo,
        };
        try {
            return await this._request(pathname, method, body);
        } catch (e) {
            throw new Error(e);
        }
    }
    async changeDeviceDescription(
        token: string,
        imei: string,
        description: Partial<IDeviceDescription>
    ) {
        const pathname = "device/change_description";
        const method = "POST";
        const body = {
            token,
            imei,
            description,
        };
        try {
            return await this._request(pathname, method, body);
        } catch (e) {
            throw new Error(e);
        }
    }
    async deleteDevice(token: string, imei: string) {
        const pathname = "device/remove";
        const method = "POST";
        const body = {
            token,
            imei,
        };
        try {
            return await this._request(pathname, method, body);
        } catch (e) {
            throw new Error(e);
        }
    }
    async getDeviceSettings(token: string, imei: string) {
        const pathname = "device/get_settings";
        const method = "POST";
        const body = {
            token,
            imei,
        };
        try {
            return await this._request<IGetDeviceSettingsRes>(
                pathname,
                method,
                body
            );
        } catch (e) {
            throw new Error(e);
        }
    }
    async changeDeviceSettings(
        token: string,
        imei: string,
        settings: IRawSettings
    ) {
        const pathname = "device/change_settings";
        const method = "POST";
        const body = {
            token,
            imei,
            settings,
        };
        try {
            return await this._request(pathname, method, body);
        } catch (e) {
            throw new Error(e);
        }
    }
    async addDeviceInDb({
        token,
        imei,
        type,
        password,
        hw_version,
        bootloader_version_major,
        bootloader_version_minor,
        application_version_major,
        application_version_minor,
    }: IAddDeviceToDbReq) {
        const pathname = "device/create";
        const method = "POST";
        const body = {
            token,
            imei,
            type,
            password,
            hw_version,
            bootloader_version_major,
            bootloader_version_minor,
            application_version_major,
            application_version_minor,
        };
        try {
            return await this._request(pathname, method, body);
        } catch (e) {
            throw new Error(e);
        }
    }
}

export const devicesAPI = new DevicesAPI(urlConfig);
